// 必要なモジュールを読み込み
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";

export class ThreeApp {
  /**
   * カメラ定義のための定数
   */
  static CAMERA_PARAM = {
    fovy: 22,
    aspect: window.innerWidth / window.innerHeight,
    near: 0.1,
    far: 200.0,
    position: new THREE.Vector3(5.0, 8.0, 30.0),
    lookAt: new THREE.Vector3(0.0, -6.0, 0.0),
  };
  /**
   * レンダラー定義のための定数
   */
  static RENDERER_PARAM = {
    clearColor: 0xffffff,
  };
  static LIGHT_PARAM = {
    color: 0xffffff,
    intensity: 6.0,
  };
  static POINT_LIGHT_PARAM = {
    color1: 0xffffff, // オレンジ色
    color2: 0xffffff, // 青色
    intensity: 4.0,
    distance: 25.0,
    decay: 1.5,
  };
  static SPOT_LIGHT_PARAM = {
    color: 0xffffff,
    intensity: 8.0,
    distance: 30.0,
    angle: Math.PI / 6, // 30度
    penumbra: 0.1,
    decay: 1.0,
  };

  canvas: HTMLCanvasElement; // 既存のcanvas要素
  renderer!: THREE.WebGLRenderer; // レンダラ
  scene!: THREE.Scene; // シーン
  camera!: THREE.PerspectiveCamera; // カメラ
  inuGltf!: any; // 読み込んだ inu glTF 格納用
  groundMesh!: THREE.Mesh; // 地面用の立方体メッシュ
  directionalLight!: THREE.DirectionalLight; // 方向性ライト
  pointLight1!: THREE.PointLight; // ポイントライト1
  pointLight2!: THREE.PointLight; // ポイントライト2
  spotLight!: THREE.SpotLight; // スポットライト（犬専用）
  mixer!: THREE.AnimationMixer; // アニメーションミキサー
  actions!: THREE.AnimationAction[]; // アニメーションのアクション
  clock!: THREE.Clock; // アニメーション用のクロック

  // 犬の移動制御用プロパティ
  pressedKeys: Set<string> = new Set(); // 押されているキー
  moveSpeed: number = 0.02; // 移動速度
  coliBounds: { min: THREE.Vector3; max: THREE.Vector3 } | null = null; // coliオブジェクトの境界
  isMoving: boolean = false; // 移動中かどうか

  /**
   * コンストラクタ
   * @constructor
   * @param {HTMLCanvasElement} canvas - 既存のcanvas要素
   */
  constructor(canvas: HTMLCanvasElement) {
    // 既存のcanvas要素を保持
    this.canvas = canvas;

    // this のバインド
    this.render = this.render.bind(this);

    // ウィンドウのリサイズを検出できるようにする
    window.addEventListener(
      "resize",
      () => {
        this.updateSize();
      },
      false
    );

    // キーボードイベントリスナーを追加
    this.setupKeyboardControls();
  }

  /**
   * サイズを更新する処理
   */
  updateSize() {
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * 初期化処理
   */
  init() {
    // canvas要素の存在確認
    if (!this.canvas) {
      throw new Error("Canvas要素が見つかりません");
    }

    // canvas要素のサイズを取得
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    // レンダラー（既存のcanvasを使用）
    const color = new THREE.Color(ThreeApp.RENDERER_PARAM.clearColor);
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.setClearColor(color);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(width, height);

    // シャドウマッピングを有効化
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // シーン
    this.scene = new THREE.Scene();

    // カメラ
    this.camera = new THREE.PerspectiveCamera(
      ThreeApp.CAMERA_PARAM.fovy,
      width / height,
      ThreeApp.CAMERA_PARAM.near,
      ThreeApp.CAMERA_PARAM.far
    );
    this.camera.position.copy(ThreeApp.CAMERA_PARAM.position);
    this.camera.lookAt(ThreeApp.CAMERA_PARAM.lookAt);

    // 環境光を追加（明るくする）
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
    this.scene.add(ambientLight);

    // 方向性ライトを追加（シャドウ対応）
    this.directionalLight = new THREE.DirectionalLight(
      ThreeApp.LIGHT_PARAM.color,
      ThreeApp.LIGHT_PARAM.intensity
    );
    this.directionalLight.position.set(5, 10, 5);

    // シャドウマップの設定
    this.directionalLight.castShadow = true;
    this.directionalLight.shadow.mapSize.width = 2048;
    this.directionalLight.shadow.mapSize.height = 2048;
    this.directionalLight.shadow.camera.near = 0.5;
    this.directionalLight.shadow.camera.far = 50;
    this.directionalLight.shadow.camera.left = -10;
    this.directionalLight.shadow.camera.right = 10;
    this.directionalLight.shadow.camera.top = 10;
    this.directionalLight.shadow.camera.bottom = -10;

    this.scene.add(this.directionalLight);

    // ポイントライト1（オレンジ色）を追加
    this.pointLight1 = new THREE.PointLight(
      ThreeApp.POINT_LIGHT_PARAM.color1,
      ThreeApp.POINT_LIGHT_PARAM.intensity,
      ThreeApp.POINT_LIGHT_PARAM.distance,
      ThreeApp.POINT_LIGHT_PARAM.decay
    );
    this.pointLight1.position.set(-3, 2, 1); // 左側に配置
    this.pointLight1.castShadow = true;
    this.pointLight1.shadow.mapSize.width = 1024;
    this.pointLight1.shadow.mapSize.height = 1024;
    this.pointLight1.shadow.camera.near = 0.1;
    this.pointLight1.shadow.camera.far = 25;
    this.scene.add(this.pointLight1);

    // ポイントライト2（青色）を追加
    this.pointLight2 = new THREE.PointLight(
      ThreeApp.POINT_LIGHT_PARAM.color2,
      ThreeApp.POINT_LIGHT_PARAM.intensity,
      ThreeApp.POINT_LIGHT_PARAM.distance,
      ThreeApp.POINT_LIGHT_PARAM.decay
    );
    this.pointLight2.position.set(3, 2, 1); // 右側に配置
    this.pointLight2.castShadow = true;
    this.pointLight2.shadow.mapSize.width = 1024;
    this.pointLight2.shadow.mapSize.height = 1024;
    this.pointLight2.shadow.camera.near = 0.1;
    this.pointLight2.shadow.camera.far = 25;
    this.scene.add(this.pointLight2);

    // スポットライト（犬専用）を追加
    this.spotLight = new THREE.SpotLight(
      ThreeApp.SPOT_LIGHT_PARAM.color,
      ThreeApp.SPOT_LIGHT_PARAM.intensity,
      ThreeApp.SPOT_LIGHT_PARAM.distance,
      ThreeApp.SPOT_LIGHT_PARAM.angle,
      ThreeApp.SPOT_LIGHT_PARAM.penumbra,
      ThreeApp.SPOT_LIGHT_PARAM.decay
    );
    this.spotLight.position.set(0, 8, 2); // 犬の真上から照らす
    this.spotLight.target.position.set(0, 0, 0); // 犬の位置をターゲット
    this.spotLight.castShadow = true;
    this.spotLight.shadow.mapSize.width = 2048;
    this.spotLight.shadow.mapSize.height = 2048;
    this.spotLight.shadow.camera.near = 0.1;
    this.spotLight.shadow.camera.far = 30;
    this.scene.add(this.spotLight);
    this.scene.add(this.spotLight.target);

    // クロックを初期化
    this.clock = new THREE.Clock();
  }
  /**
   * アセット（素材）のロードを行う Promise
   */
  load() {
    return new Promise<void>((resolve) => {
      const canvasEl = document.getElementById("webgl-canvas") as HTMLCanvasElement | null;
      const assetsBase = (canvasEl?.dataset.assetsBase || "").replace(/\/$/, "");

      // DRACOLoaderを設定
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("https://www.gstatic.com/draco/v1/decoders/");

      // GLTFLoaderにDRACOLoaderを設定
      const loader = new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);

      let loadedCount = 0;
      const totalModels = 1;

      // 両方のモデルが読み込まれたら完了
      const checkComplete = () => {
        loadedCount++;
        if (loadedCount >= totalModels) {
          dracoLoader.dispose();
          resolve();
        }
      };

      // inu.glbの読み込み
      const inuPath = `${assetsBase}/images/pages/work-027/inu.glb`;
      loader.load(
        inuPath,
        (gltf) => {
          this.inuGltf = gltf;
          checkComplete();
        },
        (_progress) => {},
        (_error) => {
          checkComplete();
        }
      );
    });
  }

  /**
   * GLTFモデルをシーンに追加する処理
   */
  setupModel() {
    if (!this.inuGltf || !this.scene) {
      return;
    }

    // 緑色の地面用立方体を作成
    const groundGeometry = new THREE.BoxGeometry(7, 4, 7); // 幅6, 高さ0.2, 奥行き6
    const groundMaterial = new THREE.MeshLambertMaterial({ color: 0x727272 }); // 渋い緑色
    this.groundMesh = new THREE.Mesh(groundGeometry, groundMaterial);

    // 地面を配置（Y座標を0に）
    this.groundMesh.position.set(0, -4.3, 3); // 地面の中心をY=-0.1に
    this.groundMesh.receiveShadow = true; // 影を受け取る

    // シーンに地面を追加
    this.scene.add(this.groundMesh);

    // inuモデルをシーンに追加
    this.scene.add(this.inuGltf.scene);

    // inuモデルにシャドウ設定を追加
    this.inuGltf.scene.traverse((child: any) => {
      if (child.isMesh) {
        child.castShadow = true; // 影を落とす
        child.receiveShadow = true; // 影を受け取る
      }
    });

    // inuモデルのバウンディングボックスを計算
    const inuBox = new THREE.Box3().setFromObject(this.inuGltf.scene);
    const inuCenter = inuBox.getCenter(new THREE.Vector3());
    const inuSize = inuBox.getSize(new THREE.Vector3());

    // inuモデルを地面の上に配置
    this.inuGltf.scene.position.sub(inuCenter);
    this.inuGltf.scene.position.y = -inuSize.y / 2 - 0.2; // 地面の上に配置

    // inuモデルが大きすぎる場合は縮小
    const inuMaxDimension = Math.max(inuSize.x, inuSize.y, inuSize.z);
    const inuScale = inuMaxDimension > 2 ? 2 / inuMaxDimension : 1;
    this.inuGltf.scene.scale.setScalar(inuScale);

    // 境界を手動設定（地面のサイズに合わせて）
    this.coliBounds = {
      min: new THREE.Vector3(-3, -10, -0.2),
      max: new THREE.Vector3(3, 10, 6),
    };

    // アニメーションを設定
    this.setupAnimation();
  }

  /**
   * キーボードコントロールの設定
   */
  setupKeyboardControls() {
    // キーが押された時の処理
    document.addEventListener("keydown", (event) => {
      const key = event.key.toLowerCase();
      if (["w", "a", "s", "d"].includes(key)) {
        this.pressedKeys.add(key);
        event.preventDefault(); // デフォルトの動作を防ぐ
      }
    });

    // キーが離された時の処理
    document.addEventListener("keyup", (event) => {
      const key = event.key.toLowerCase();
      if (["w", "a", "s", "d"].includes(key)) {
        this.pressedKeys.delete(key);
        event.preventDefault(); // デフォルトの動作を防ぐ
      }
    });
  }

  /**
   * 犬がcoliの境界内にいるかチェック
   */
  isWithinBounds(position: THREE.Vector3): boolean {
    if (!this.coliBounds) {
      return true; // 境界が設定されていない場合は移動可能
    }

    const withinX = position.x >= this.coliBounds.min.x && position.x <= this.coliBounds.max.x;
    const withinZ = position.z >= this.coliBounds.min.z && position.z <= this.coliBounds.max.z;

    return withinX && withinZ;
  }

  /**
   * 犬の移動処理
   */
  updateDogMovement() {
    if (!this.inuGltf || !this.inuGltf.scene) {
      return;
    }

    const dog = this.inuGltf.scene;

    // 移動状態を更新
    const wasMoving = this.isMoving;
    this.isMoving = this.pressedKeys.size > 0;

    // 移動状態が変わったらアニメーションを制御
    if (wasMoving !== this.isMoving) {
      this.controlAnimation();
    }

    // WASDキーに応じて移動（境界チェック付き）
    const originalPosition = dog.position.clone();
    let hasMoved = false;

    if (this.pressedKeys.has("w")) {
      dog.position.z -= this.moveSpeed; // 前進
      if (!this.isWithinBounds(dog.position)) {
        dog.position.copy(originalPosition); // 移動を取り消し
      } else {
        hasMoved = true;
      }
    }
    if (this.pressedKeys.has("s")) {
      dog.position.z += this.moveSpeed; // 後退
      if (!this.isWithinBounds(dog.position)) {
        dog.position.copy(originalPosition); // 移動を取り消し
      } else {
        hasMoved = true;
      }
    }
    if (this.pressedKeys.has("a")) {
      dog.position.x -= this.moveSpeed; // 左移動
      if (!this.isWithinBounds(dog.position)) {
        dog.position.copy(originalPosition); // 移動を取り消し
      } else {
        hasMoved = true;
      }
    }
    if (this.pressedKeys.has("d")) {
      dog.position.x += this.moveSpeed; // 右移動
      if (!this.isWithinBounds(dog.position)) {
        dog.position.copy(originalPosition); // 移動を取り消し
      } else {
        hasMoved = true;
      }
    }

    // 実際に移動したかどうかでアニメーション制御
    if (hasMoved !== this.isMoving) {
      this.isMoving = hasMoved;
      this.controlAnimation();
    }

    // 移動方向に応じて犬の向きを調整（クオータニオン使用）
    if (this.pressedKeys.size > 0) {
      let targetDirection = new THREE.Vector3();

      if (this.pressedKeys.has("w") && this.pressedKeys.has("a")) {
        targetDirection.set(-1, 0, -1).normalize(); // 左上
      } else if (this.pressedKeys.has("w") && this.pressedKeys.has("d")) {
        targetDirection.set(1, 0, -1).normalize(); // 右上
      } else if (this.pressedKeys.has("s") && this.pressedKeys.has("a")) {
        targetDirection.set(-1, 0, 1).normalize(); // 左下
      } else if (this.pressedKeys.has("s") && this.pressedKeys.has("d")) {
        targetDirection.set(1, 0, 1).normalize(); // 右下
      } else if (this.pressedKeys.has("w")) {
        targetDirection.set(0, 0, -1); // 上
      } else if (this.pressedKeys.has("s")) {
        targetDirection.set(0, 0, 1); // 下
      } else if (this.pressedKeys.has("a")) {
        targetDirection.set(-1, 0, 0); // 左
      } else if (this.pressedKeys.has("d")) {
        targetDirection.set(1, 0, 0); // 右
      }

      // 現在の前方向ベクトル（犬の初期向き）
      const currentDirection = new THREE.Vector3(0, 0, 1);
      currentDirection.applyQuaternion(dog.quaternion);

      // クオータニオンを計算して滑らかに回転
      const quaternion = new THREE.Quaternion();
      quaternion.setFromUnitVectors(currentDirection, targetDirection);

      // 現在のクオータニオンと目標クオータニオンを球面線形補間
      dog.quaternion.slerp(quaternion.clone().multiply(dog.quaternion), 0.1);
    }
  }

  /**
   * アニメーションの再生/停止制御
   */
  controlAnimation() {
    if (!this.actions || this.actions.length === 0) {
      return;
    }

    this.actions.forEach((action) => {
      if (this.isMoving) {
        action.paused = false; // 移動中は再生
      } else {
        action.paused = true; // 停止中は一時停止
      }
    });
  }

  /**
   * アニメーションの設定
   */
  setupAnimation() {
    if (!this.inuGltf || !this.inuGltf.animations || this.inuGltf.animations.length === 0) {
      return;
    }

    // アニメーションミキサーを作成（inuモデル用）
    this.mixer = new THREE.AnimationMixer(this.inuGltf.scene);
    this.actions = [];

    // アニメーションを設定（最初のアニメーションを使用）
    const animation = this.inuGltf.animations[0];
    const action = this.mixer.clipAction(animation);

    // アニメーションをループ設定
    action.setLoop(THREE.LoopRepeat, Infinity);

    // アニメーションを開始（最初は停止状態）
    action.play();
    action.paused = true; // 最初は停止状態

    this.actions.push(action);
  }

  /**
   * 描画処理
   */
  render() {
    // 恒常ループ
    requestAnimationFrame(this.render);

    // アニメーションを更新
    if (this.mixer) {
      this.mixer.update(this.clock.getDelta());
    }

    // 犬の移動処理を更新
    this.updateDogMovement();

    // 通常のレンダリング
    this.renderer.render(this.scene, this.camera);
  }
}
